/**
 * SSR-Misuse Sentinel
 *
 * Reports calls to browser-only service methods that happen during
 * server-side rendering of `'use client'` components. Hand-written
 * sentinel implementations call `reportSSRMisuse`
 * from every method, naming the call site so contributors can find
 * the misuse without further debugging.
 *
 * Environment-conditional behavior:
 * - Non-production: throws an Error so the misuse surfaces during
 *   local dev, CI, and any non-prod environment.
 * - Production: emits a structured warn via the module-scoped logger
 *   `baseLogger.getLogger({ module: 'ssr-misuse' })` so the misuse
 *   becomes searchable in Datadog without crashing the page; the
 *   sentinel method then returns its interface-appropriate value.
 */

import { baseLogger } from './logger/base'

const SSR_MISUSE_CODE = 'CLIENT_SSR_MISUSE'
const SSR_MISUSE_ANCHOR = 'Browser-only method called during SSR'

const ssrMisuseLogger = baseLogger.getLogger({ module: 'ssr-misuse' })

function formatMessage(method: string): string {
    return [
        `${method} called during SSR.`,
        `'use client' components prerender on the server before hydrating in the browser.`,
        `Move this call to a useEffect, an event handler, or other client-only code path.`,
    ].join(' ')
}

export function reportSSRMisuse(method: string): void {
    if (process.env.NODE_ENV !== 'production') {
        throw new Error(formatMessage(method))
    }
    ssrMisuseLogger.withMetadata({ method, code: SSR_MISUSE_CODE }).warn(SSR_MISUSE_ANCHOR)
}
